import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, useForwardingRef } from '@arc/frontend/util-common';
import { FormControl } from '../../FormControl';
import { SelectorDateTime } from './SelectorDateTime';
/**
 * A react-hook-form input which works over a valid ISO date string.
 */
export function SelectorDateTimeWithControl(props) {
  const {
    name,
    label,
    helperStyle,
    helperExtra,
    outerStyle,
    ...other
  } = props;
  const {
    onError,
    selectorErrorMessage,
    selectorError
  } = useDateTimeSelectorError(other);
  const {
    field: {
      onChange,
      value,
      ref
    },
    fieldState: {
      invalid
    },
    errorMessage
  } = useField({
    name
  });
  const inputRef = useForwardingRef(null, [ref, props.inputRef]);
  const error = invalid || selectorError;
  return <FormControl label={label} error={error} helperText={selectorErrorMessage ?? errorMessage} helperStyle={helperStyle} helperExtra={helperExtra || undefined} outerStyle={outerStyle}>
			<SelectorDateTime {...other} inputRef={inputRef} value={value ? DateTime.fromISO(value) : null} onChange={(newValue, context) => {
      if (newValue === null) {
        onChange('');
        return;
      }
      if (!context.validationError) {
        onChange(newValue.toISO());
      }
    }} onError={onError} error={error} />
		</FormControl>;
}
export function useDateTimeSelectorError(props) {
  const {
    t
  } = useTranslation();
  const selectorErrorMessages = useMemo(() => ({
    disableFuture: t('validations:DateTime disableFuture'),
    disablePast: t('validations:DateTime disablePast'),
    shouldDisableDate: t('validations:DateTime shouldDisableDate'),
    shouldDisableMonth: t('validations:DateTime shouldDisableMonth'),
    shouldDisableYear: t('validations:DateTime shouldDisableYear'),
    minDate: props?.minDate ? t('validations:DateTime minDate', {
      minDate: props.minDate.toLocaleString(DateTime.DATE_FULL)
    }) : t('validations:DateTime minDate generic'),
    maxDate: props?.maxDate ? t('validations:DateTime maxDate', {
      maxDate: props.maxDate.toLocaleString(DateTime.DATE_FULL)
    }) : t('validations:DateTime maxDate generic'),
    minutesStep: t('validations:DateTime minutesStep', {
      minutesStep: props?.minutesStep
    }),
    minTime: t('validations:DateTime minTime', {
      minTime: props?.minTime?.toLocaleString(DateTime.TIME_SIMPLE)
    }),
    maxTime: t('validations:DateTime maxTime', {
      maxTime: props?.maxTime?.toLocaleString(DateTime.TIME_SIMPLE)
    }),
    'shouldDisableTime-hours': t('validations:DateTime shouldDisableTime-hours'),
    'shouldDisableTime-minutes': t('validations:DateTime shouldDisableTime-minutes'),
    'shouldDisableTime-seconds': t('validations:DateTime shouldDisableTime-seconds')
  }), [props?.maxDate, props?.maxTime, props?.minDate, props?.minTime, props?.minutesStep, t]);
  const [selectorErrorState, setSelectorErrorState] = useState(null);
  const selectorError = !!selectorErrorState && selectorErrorState !== 'invalidDate';
  const selectorErrorMessage = selectorError ? selectorErrorMessages[selectorErrorState] : null;
  return {
    onError: error => setSelectorErrorState(error),
    selectorErrorMessage,
    selectorError
  };
}